import { Form, Link, useActionData, useNavigation, useParams, useSearchParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { HiddenResult, SpaceEvent } from "@repo/shared-types";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/session";
import { loadSpaceContext, useSpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { tenantPath } from "@/lib/tenants";
import { formatMoment } from "@/lib/times";
import { toast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyNote } from "@/components/empty-note";
import { EyeOff } from "lucide-react";
import { ErrorNote } from "@/components/form";
import { ResultFields } from "@/components/leaderboard";
import { ReviewItem } from "@/components/review-item";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";

/**
 * An event's hidden results: every result the owner, or an admin holding
 * moderate, hid on one part's board (0026), the most recently hidden first, a
 * page at a time, with who hid each. Putting one back shows it on its board
 * at once, and takes the line about it out of its athlete's inbox.
 *
 * Only results still shared are here: one its athlete took down is on no
 * board to put back on, and comes back hidden if they share it again.
 *
 * Each item is laid out as the queue's are, Put back where Verify is there.
 *
 * Anybody else is told whose page this is; the api refuses them the list,
 * whatever this page renders.
 */

export async function hiddenLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const cursor = new URL(request.url).searchParams.get("cursor");
  const [context] = await Promise.all([loadSpaceContext(request, slug), read(queries.event(slug, eventId))]);
  if (context.canModerate) await read(queries.hidden(slug, eventId, { cursor }));
  return null;
}

/** Nothing once it has worked — a toast says so — or why not, which stays on the page. */
type ActionResult = { error?: string };

export async function hiddenAction({ request, params }: ActionFunctionArgs): Promise<ActionResult> {
  const form = await request.formData();
  const events = api.space(params.slug ?? "").events;
  try {
    switch (String(form.get("intent") ?? "")) {
      case "unhide":
        // A team's, by its id, in place of an athlete's; a workout's photo, by
        // its result.
        if (form.get("photo")) await events.results.unhidePhoto(params.event ?? "", String(form.get("entry") ?? ""));
        else if (form.get("team")) await events.teams.unhide(params.event ?? "", String(form.get("team")));
        else await events.unhide(params.event ?? "", String(form.get("part") ?? ""), String(form.get("entry") ?? ""));
        toast("Put back.");
        return {};
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function HiddenPage() {
  const params = useParams();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const { tenant, canModerate, zone } = useSpaceContext(slug);
  const { data: event } = useSuspenseQuery(queries.event(slug, eventId));
  const cursor = useSearchParams()[0].get("cursor");
  // Read by the loader for those who moderate; nobody else asks.
  const hidden = useQuery({ ...queries.hidden(slug, eventId, { cursor }), enabled: canModerate }).data ?? null;
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const back = `${tenantPath(tenant)}/events/${event.id}`;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={back}>{event.title}</BackLink>
        <PageTitle of={["Hidden results", event.title]} />
        <h1 className="page-title">Hidden results</h1>
        {hidden && <p className="text-muted-foreground">{hidden.hidden === 1 ? "1 hidden" : `${hidden.hidden} hidden`}</p>}
      </div>

      {result?.error && <ErrorNote message={result.error} />}

      {!canModerate || !hidden ? (
        <Alert role="note">
          <AlertDescription>
            Only the owner, and admins the owner has given Moderate, hide results and put them back.
          </AlertDescription>
        </Alert>
      ) : hidden.items.length === 0 && !cursor ? (
        <EmptyNote icon={EyeOff}>Nothing is hidden on this event's leaderboards.</EmptyNote>
      ) : (
        <Card className="gap-0 py-0">
          <ol className="divide-y divide-border" aria-label="Hidden">
            {hidden.items.map((item) => (
              <Item
                key={item.team ? item.team.id : item.photo ? `${item.entryId}-photo` : `${item.entryId}-${item.partId}`}
                item={item}
                event={event}
                zone={zone}
                busy={busy}
              />
            ))}
          </ol>
        </Card>
      )}

      {hidden && (cursor || hidden.nextCursor) && (
        <nav className="flex justify-between" aria-label="Pages">
          {cursor ? (
            <Button asChild variant="outline" size="lg">
              <Link to="?">← First</Link>
            </Button>
          ) : (
            <span />
          )}
          {hidden.nextCursor && (
            <Button asChild variant="outline" size="lg">
              <Link to={`?cursor=${encodeURIComponent(hidden.nextCursor)}`}>Next →</Link>
            </Button>
          )}
        </nav>
      )}
    </section>
  );
}

/**
 * One hidden result: its part and board, the athlete or the team, the result,
 * who hid it and when, and Put back — beside See it for a photo, which has no
 * score to show here.
 */
function Item({ item, event, zone, busy }: { item: HiddenResult; event: SpaceEvent; zone: string; busy: boolean }) {
  const team = item.team;
  const by = item.hiddenBy ? `@${item.hiddenBy.username}` : "a deleted account";

  return (
    <ReviewItem
      event={event}
      item={item}
      photo={item.photo}
      when={
        <>
          {team ? "a team, " : ""}hidden by {by}, {formatMoment(item.hiddenAt, zone)}
        </>
      }
      actions={
        <>
          <Form method="post" className="shrink-0">
            <input type="hidden" name="intent" value="unhide" />
            {item.photo && <input type="hidden" name="photo" value="1" />}
            <ResultFields partId={item.partId} entryId={item.entryId} teamId={team?.id} />
            <Button type="submit" size="sm" disabled={busy}>
              Put back
            </Button>
          </Form>
          {item.photo && (
            <Button asChild variant="outline" size="sm">
              <Link to={`../results/${item.entryId}`} relative="path">
                See it
              </Link>
            </Button>
          )}
        </>
      }
    />
  );
}
